import { EVIDENCE_EVENT_KINDS, type EvidenceEventKind } from '../../domain/evidence/evidenceController'
import { evidenceProvider } from '../../integrations/supabase/evidenceProvider'
import { mapProviderError, type AppResult } from '../platform/public'

export type EvidenceHistoryRow = Readonly<{
  eventKind: EvidenceEventKind
  eventId: string
  actorType: string
  actorId: string
  actionOrObservation: string
  targetType: string
  targetId: string
  decisionOrResult: string
  safeReasonCode: string | null
  policyVersion: number | null
  eventVersion: number
  provenance: string
  correlationId: string
  occurredAt: string
  ingestedAt: string
}>

export type EvidenceMetricSnapshot = Readonly<{
  total: number
  complete: number
  completenessRatio: number
  errorCount: number
  maxIngestionLagMs: number
}>

function isEvidenceKind(value: unknown): value is EvidenceEventKind {
  return typeof value === 'string' && EVIDENCE_EVENT_KINDS.includes(value as EvidenceEventKind)
}

function numeric(value: unknown): number {
  if (typeof value === 'number') return value
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value)
  return 0
}

export const evidenceApi = {
  async history(input: {
    tenantId: string
    objectType?: string
    objectId?: string
    actorId?: string
    eventKind?: EvidenceEventKind
    eventVersion?: number
    limit?: number
  }): Promise<AppResult<EvidenceHistoryRow[]>> {
    const result = await evidenceProvider.history(input)
    if (result.error) return { data: null, error: mapProviderError(result.error) }
    const rows = (result.data ?? []) as Array<Record<string, unknown>>
    return {
      data: rows.flatMap((row) => {
        if (!isEvidenceKind(row.event_kind)) return []
        return [{
          eventKind: row.event_kind,
          eventId: String(row.event_id),
          actorType: String(row.actor_type),
          actorId: String(row.actor_id),
          actionOrObservation: String(row.action_or_observation),
          targetType: String(row.target_type),
          targetId: String(row.target_id),
          decisionOrResult: String(row.decision_or_result),
          safeReasonCode: typeof row.safe_reason_code === 'string' ? row.safe_reason_code : null,
          policyVersion: row.policy_version === null || row.policy_version === undefined ? null : numeric(row.policy_version),
          eventVersion: numeric(row.event_version),
          provenance: String(row.provenance),
          correlationId: String(row.correlation_id),
          occurredAt: String(row.occurred_at),
          ingestedAt: String(row.ingested_at),
        }]
      }),
      error: null,
    }
  },

  async metrics(tenantId: string, since?: string): Promise<AppResult<EvidenceMetricSnapshot>> {
    const result = await evidenceProvider.metrics(tenantId, since)
    if (result.error || !result.data) return { data: null, error: mapProviderError(result.error) }
    const row = result.data as Record<string, unknown>
    return {
      data: {
        total: numeric(row.total),
        complete: numeric(row.complete),
        completenessRatio: numeric(row.completeness_ratio),
        errorCount: numeric(row.error_count),
        maxIngestionLagMs: numeric(row.max_ingestion_lag_ms),
      },
      error: null,
    }
  },
}
